import { useEffect, useState } from "react";
import { http } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Lock, Send } from "lucide-react";

const UFS = ["AC","AL","AP","AM","BA","CE","DF","ES","GO","MA","MT","MS","MG","PA","PB","PR","PE","PI","RJ","RN","RS","RO","RR","SC","SP","SE","TO"];

const STATUS = {
  pending: { txt: "Aguardando análise", cls: "bg-amber-100 text-amber-700 border-amber-200" },
  approved: { txt: "Aprovado", cls: "bg-emerald-100 text-emerald-700 border-emerald-200" },
  rejected: { txt: "Não aprovado", cls: "bg-rose-100 text-rose-700 border-rose-200" },
};

// Lote 87 — para quem não é administrador, a OAB fica travada depois do cadastro;
// inclusão/remoção só por pedido, que o administrador aprova ou recusa.
// `remover` (opcional) vem preenchido quando o usuário clicou em "Solicitar remoção" numa OAB.
export default function OabSolicitacoes({ oabs, remover, onClearRemover }) {
  const [lista, setLista] = useState([]);
  const [aberto, setAberto] = useState(false);
  const [numero, setNumero] = useState("");
  const [uf, setUf] = useState("SP");
  const [motivo, setMotivo] = useState("");
  const [enviando, setEnviando] = useState(false);

  const load = () => http.get("/oab/solicitacoes").then((r) => setLista(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  useEffect(() => { if (remover) setAberto(true); }, [remover]);

  const fechar = () => { setAberto(false); setNumero(""); setMotivo(""); onClearRemover && onClearRemover(); };

  const enviar = async () => {
    setEnviando(true);
    try {
      const body = remover
        ? { tipo: "remover", oab_id: remover.oab_id, motivo }
        : { tipo: "incluir", numero, uf, motivo };
      await http.post("/oab/solicitacoes", body);
      toast.success("Pedido enviado ao administrador");
      fechar();
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Não foi possível enviar o pedido");
    } finally {
      setEnviando(false);
    }
  };

  const pronto = motivo.trim().length >= 5 && (remover || numero.replace(/\D/g, "").length >= 2);

  return (
    <div data-testid="oab-travada" className="mb-6 rounded-md border border-stone-200 bg-stone-50 p-4 space-y-3">
      <div className="flex items-start gap-3">
        <Lock className="w-4 h-4 text-stone-500 mt-0.5 shrink-0" strokeWidth={2} />
        <div className="text-sm text-stone-600 leading-relaxed">
          A OAB informada no cadastro fica <strong>travada</strong>. Para incluir outra OAB (ou remover
          uma existente), envie um pedido — o administrador analisa e aprova ou recusa.
        </div>
      </div>

      {!aberto ? (
        <Button data-testid="oab-solicitar-abrir" variant="outline" size="sm" onClick={() => setAberto(true)} className="gap-2">
          <Send className="w-3.5 h-3.5" strokeWidth={2} /> Solicitar nova OAB
        </Button>
      ) : (
        <div data-testid="oab-solicitar-form" className="space-y-3 bg-white border border-stone-200 rounded-md p-4">
          <div className="text-sm font-medium text-stone-900">
            {remover ? `Solicitar remoção da OAB ${remover.numero}/${remover.uf}` : "Solicitar inclusão de OAB"}
          </div>
          {!remover && (
            <div className="flex flex-wrap gap-3">
              <div className="flex-1 min-w-[140px]">
                <Label className="text-xs text-stone-500 mb-1.5 block">Número da OAB</Label>
                <Input data-testid="oab-sol-numero" inputMode="numeric" placeholder="Ex: 123456"
                  value={numero} onChange={(e) => setNumero(e.target.value.replace(/\D/g, ""))} />
              </div>
              <div className="w-24">
                <Label className="text-xs text-stone-500 mb-1.5 block">UF</Label>
                <Select value={uf} onValueChange={setUf}>
                  <SelectTrigger data-testid="oab-sol-uf"><SelectValue /></SelectTrigger>
                  <SelectContent>{UFS.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
          )}
          <div>
            <Label className="text-xs text-stone-500 mb-1.5 block">Motivo do pedido</Label>
            <Input data-testid="oab-sol-motivo" maxLength={500}
              placeholder="Ex: inscrição suplementar em outro estado"
              value={motivo} onChange={(e) => setMotivo(e.target.value)} />
          </div>
          <div className="flex gap-2">
            <Button data-testid="oab-sol-enviar" size="sm" disabled={!pronto || enviando} onClick={enviar}
              className="bg-stone-900 hover:bg-stone-800 text-white">
              {enviando ? "Enviando…" : "Enviar pedido"}
            </Button>
            <Button size="sm" variant="ghost" onClick={fechar}>Cancelar</Button>
          </div>
        </div>
      )}

      {lista.length > 0 && (
        <ul data-testid="oab-sol-lista" className="space-y-1.5">
          {lista.slice(0, 6).map((s) => {
            const st = STATUS[s.status] || STATUS.pending;
            return (
              <li key={s.solicitacao_id} className="flex flex-wrap items-center gap-2 text-xs text-stone-600">
                <span className={`px-2 py-0.5 rounded border font-medium ${st.cls}`}>{st.txt}</span>
                <span className="tabular-nums">
                  {s.tipo === "incluir" ? "Incluir" : "Remover"} OAB {s.numero}/{s.uf}
                </span>
                {s.decision_reason && <span className="text-stone-500">— {s.decision_reason}</span>}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
